/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import type { Meta, StoryObj } from '@storybook/react-vite';

import { Response } from '../Response';
import {
  ResponseAllComponents,
  ResponseAllComponentsSource,
  ResponseCodeExamples,
  ResponseCodeExamplesSource,
  ResponseDefault,
  ResponseDefaultSource,
  ResponseFullExample,
  ResponseFullExampleSource,
  ResponseHeadings,
  ResponseHeadingsSource,
  ResponseLists,
  ResponseListsSource,
  ResponseStreaming,
  ResponseStreamingSource,
} from './index';

const meta: Meta<typeof Response> = {
  component: Response,
  id: 'components-ai-response',
  parameters: {
    controls: { disable: true },
  },
  tags: ['docsPage'],
  title: 'Components/AI/Response',
};

export default meta;
type Story = StoryObj<typeof Response>;

export const Default: Story = {
  args: {},
  parameters: {
    docs: {
      source: {
        code: ResponseDefaultSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseDefault,
};

export const Streaming: Story = {
  args: {},
  parameters: {
    chromatic: { disableSnapshot: true },
    docs: {
      source: {
        code: ResponseStreamingSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseStreaming,
};

export const AllComponents: Story = {
  args: {},
  parameters: {
    docs: {
      source: {
        code: ResponseAllComponentsSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseAllComponents,
};

export const CodeExamples: Story = {
  args: {},
  parameters: {
    docs: {
      source: {
        code: ResponseCodeExamplesSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseCodeExamples,
};

export const Headings: Story = {
  args: {},
  parameters: {
    docs: {
      source: {
        code: ResponseHeadingsSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseHeadings,
};

export const Lists: Story = {
  args: {},
  parameters: {
    docs: {
      source: {
        code: ResponseListsSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseLists,
};

export const FullExample: Story = {
  args: {},
  decorators: [
    (Story) => (
      <div className="n-bg-neutral-bg-default n-p-4">
        <Story />
      </div>
    ),
  ],
  parameters: {
    docs: {
      source: {
        code: ResponseFullExampleSource,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: ResponseFullExample,
};
